import React, { useState, useEffect } from 'react';
import { 
  Terminal, 
  RotateCw, 
  CheckCircle2, 
  Eye, 
  EyeOff, 
  Key, 
  Play, 
  Check, 
  AlertTriangle,
  AlertCircle,
  Loader2,
  Sparkles,
  ExternalLink,
  Clock,
  HardDrive,
  Copy,
  ChevronDown,
  ChevronUp,
  Code2,
  ShieldCheck,
  Zap
} from 'lucide-react';
import { 
  executePortalScrape, 
  getStoredCredentials, 
  getStoredLogs, 
  getStoredPassword, 
  saveCredentials,
  saveStoredLogs, 
  ScrapeExecutionResult 
} from '../services/scraperEngine';
import { getSqliteCredentials } from '../services/sqliteDatabase';
import { PortalCredential, ScrapeLogEntry } from '../types/energy';
import { dispatchPushNotification } from '../services/notificationService';
import { Language, translations } from '../i18n/translations';

interface ScraperViewProps {
  onSyncComplete: (result: ScrapeExecutionResult) => void | Promise<void>;
  lang?: Language;
}

export function ScraperView({ onSyncComplete, lang = 'ja' }: ScraperViewProps) {
  const [credentials, setCredentials] = useState<PortalCredential>(getStoredCredentials());
  const [password, setPassword] = useState(getStoredPassword());
  const [showPassword, setShowPassword] = useState(false);
  const [isScraping, setIsScraping] = useState(false);
  const [isSavingCreds, setIsSavingCreds] = useState(false);
  const [currentStep, setCurrentStep] = useState<string>('');
  const [progressPct, setProgressPct] = useState<number>(0);
  const [logs, setLogs] = useState<ScrapeLogEntry[]>(getStoredLogs());
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [scrapeError, setScrapeError] = useState<string | null>(null);
  const [scrapeSuccessMsg, setScrapeSuccessMsg] = useState<string | null>(null);

  // cURL debugger state
  const [expandedCurlId, setExpandedCurlId] = useState<string | null>(null);
  const [copiedCurlId, setCopiedCurlId] = useState<string | null>(null);

  const t = translations[lang];

  // Refresh credentials from SQLite on mount
  useEffect(() => {
    let isMounted = true;
    getSqliteCredentials().then(({ creds, password: pw }) => {
      if (!isMounted) return;
      if (creds && (creds.email || creds.portalUrl)) {
        setCredentials((prev) => ({
          ...prev,
          ...creds,
          email: creds.email !== undefined && creds.email !== null ? creds.email : prev.email,
          portalUrl: creds.portalUrl || prev.portalUrl,
          dashboardUrl: creds.dashboardUrl || prev.dashboardUrl
        }));
      }
      if (pw) {
        setPassword(pw);
      }
    }).catch((err) => {
      console.warn('[ScraperView] Credentials sync notice:', err);
    });
    return () => {
      isMounted = false;
    };
  }, []);

  const handleSaveCredentials = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSavingCreds(true);
    setSaveMessage(null);
    try {
      await saveCredentials(credentials, password);
      setSaveSuccess(true);
      setSaveMessage(
        lang === 'ja'
          ? 'ログイン情報を安全に端末へ保存しました'
          : 'Credentials saved securely to this device'
      );
      setTimeout(() => {
        setSaveSuccess(false);
        setSaveMessage(null);
      }, 3500);
    } catch (err) {
      console.error('[ScraperView] Error saving credentials:', err);
      setSaveMessage(
        lang === 'ja'
          ? 'ログイン情報の保存に失敗しました。もう一度お試しください。'
          : 'Failed to save credentials. Please try again.'
      );
      setTimeout(() => setSaveMessage(null), 4000);
    } finally {
      setIsSavingCreds(false);
    }
  };

  const handleCopyCurl = (id: string, text: string) => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(text);
    }
    setCopiedCurlId(id);
    setTimeout(() => setCopiedCurlId(null), 2500);
  };

  /**
   * 1-Click Automated Browser Emulation Scrape
   * Uses JS fetch to emulate complete browser session:
   * GET login -> CSRF _token -> POST credentials -> 302 redirect -> dashboard/demands -> SQLite WASM
   */
  const run1ClickAutoScrape = async () => {
    const rawEmail = credentials.email?.trim() || '';
    const rawPw = password?.trim() || '';

    if (!rawEmail || !rawPw) {
      setScrapeError(
        lang === 'ja'
          ? 'ログインID（メールアドレスまたはお客さま番号）とパスワードを設定してください。'
          : 'Please enter your Login ID and password in the credentials vault below.'
      );
      setTimeout(() => setScrapeError(null), 5000);
      return;
    }

    setIsScraping(true);
    setProgressPct(0);
    setScrapeError(null);
    setScrapeSuccessMsg(null);
    setCurrentStep(lang === 'ja' ? 'ブラウザセッション確立中...' : 'Establishing browser session...');

    try {
      const result = await executePortalScrape((step, pct) => {
        setCurrentStep(step);
        setProgressPct(pct);
      }, lang);

      setLogs(result.logs);
      // Awaited: the sync only claims success once the bundle is on the device.
      await onSyncComplete(result);

      const hCount = result.hourlyUsage?.length || 0;
      const dCount = result.dailyUsage?.length || 0;
      const iCount = result.invoices?.length || 0;

      const successDetail = lang === 'ja'
        ? `同期完了！ 30分値 ${hCount}コマ、日別 ${dCount}日分、検針票 ${iCount}件を自動取得しSQLiteに保存しました。`
        : `Sync complete! Loaded ${hCount} 30-min intervals, ${dCount} days, and ${iCount} bills into SQLite.`;

      setScrapeSuccessMsg(successDetail);

      dispatchPushNotification(
        lang === 'ja' ? 'マイページ自動同期完了' : 'Portal Sync Complete',
        lang === 'ja' 
          ? `シン・エナジーよりスマートメーター30分値（${hCount}コマ）と請求明細を自動取得しました。`
          : `Auto-scraped 30-min interval meter data and statements from SymEnergy.`,
        'scrape_sync'
      );
    } catch (err: any) {
      console.error('[ScraperView] Scrape error:', err);
      setScrapeError(err instanceof Error ? err.message : String(err));
      // Re-read logs from SQLite to display failed step in terminal
      setLogs(getStoredLogs());
    } finally {
      setIsScraping(false);
    }
  };

  const handleClearLogs = () => {
    setLogs([]);
    saveStoredLogs([]);
  };

  return (
    <div className="space-y-5 pb-8">
      {/* 1-Click Automated Browser Emulation Hero Action Card */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-teal-950/40 to-slate-900 border border-teal-500/30 p-5 shadow-2xl">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-teal-500/20 border border-teal-500/40 text-teal-300 flex items-center justify-center shrink-0">
              <Zap className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base font-bold text-white">
                  {lang === 'ja' ? '1クリック自動スクレイピング' : '1-Click Automated Portal Scraper'}
                </h3>
                <span className="text-[10px] font-bold text-teal-300 bg-teal-950/80 border border-teal-500/40 rounded-full px-2 py-0.5">
                  {lang === 'ja' ? 'ブラウザ自動エミュレート' : 'Auto Browser Emulation'}
                </span>
              </div>
              <p className="text-xs text-slate-300 mt-0.5">
                {lang === 'ja'
                  ? 'JS fetchによりブラウザのログインセッション・CSRF検証・30分値スマートメーター取得を完全自動実行します。'
                  : 'Emulates full browser authentication, CSRF validation, and 30-min meter scraping in 1 click.'}
              </p>
            </div>
          </div>
        </div>

        {/* 1-Click Primary Action Button */}
        <div className="mt-5">
          <button
            type="button"
            disabled={isScraping}
            onClick={run1ClickAutoScrape}
            className="w-full h-13 bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 active:scale-[0.98] text-slate-950 font-extrabold text-sm rounded-2xl flex items-center justify-center gap-2 transition-transform shadow-xl shadow-teal-500/25 disabled:opacity-50 cursor-pointer"
          >
            {isScraping ? (
              <>
                <RotateCw className="w-5 h-5 animate-spin" />
                <span>{currentStep || (lang === 'ja' ? '自動取得実行中...' : 'Scraping in progress...')}</span>
              </>
            ) : (
              <>
                <Play className="w-5 h-5 fill-slate-950" />
                <span>
                  {lang === 'ja'
                    ? '⚡ 1クリックで今すぐマイページから自動取得'
                    : '⚡ 1-Click Auto Scrape from SymEnergy Portal'}
                </span>
              </>
            )}
          </button>
        </div>

        {/* Progress Bar during scraping */}
        {isScraping && (
          <div className="mt-4 space-y-1.5 animate-fadeIn">
            <div className="flex justify-between text-xs text-slate-300">
              <span className="text-teal-400 font-semibold">{currentStep}</span>
              <span className="font-mono font-bold text-teal-300">{progressPct}%</span>
            </div>
            <div className="w-full h-2.5 bg-slate-800 rounded-full overflow-hidden">
              <div
                style={{ width: `${progressPct}%` }}
                className="h-full bg-gradient-to-r from-teal-500 to-emerald-400 transition-all duration-300"
              />
            </div>
          </div>
        )}

        {/* Success Message Banner */}
        {scrapeSuccessMsg && (
          <div className="mt-4 p-3.5 rounded-2xl bg-emerald-950/80 border border-emerald-500/50 text-xs text-emerald-200 flex items-center gap-2.5 animate-fadeIn">
            <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
            <span className="leading-relaxed font-medium">{scrapeSuccessMsg}</span>
          </div>
        )}

        {/* Error Message Banner */}
        {scrapeError && (
          <div className="mt-4 p-3.5 rounded-2xl bg-rose-950/85 border border-rose-500/60 text-xs text-rose-200 flex items-start gap-2.5 animate-fadeIn">
            <AlertTriangle className="w-4 h-4 shrink-0 text-rose-400 mt-0.5" />
            <div className="space-y-1">
              <p className="font-bold">{lang === 'ja' ? 'スクレイピング失敗' : 'Scrape Failed'}</p>
              <p className="leading-relaxed opacity-95">{scrapeError}</p>
            </div>
          </div>
        )}

        {/* Portal Architecture Notice */}
        <div className="mt-4 p-3 rounded-2xl bg-slate-950/70 border border-slate-800 text-[11px] text-slate-400 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 truncate">
            <ShieldCheck className="w-4 h-4 text-teal-400 shrink-0" />
            <span className="truncate">
              {lang === 'ja'
                ? 'セッションCookie（XSRF / lv_sees_session）自動維持 & 端末内SQLite WASM永続化'
                : 'Auto session cookies persistence & local SQLite WASM storage'}
            </span>
          </div>
          <a
            href="https://www.symenergy.net/symenergy/login"
            target="_blank"
            rel="noreferrer"
            className="text-teal-400 hover:underline flex items-center gap-1 font-mono text-[10px] shrink-0"
          >
            symenergy.net
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>

      {/* Scheduled Auto-Sync Guarantee Card */}
      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-4 sm:p-5 shadow-xl space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-teal-500/20 text-teal-400 flex items-center justify-center">
              <Clock className="w-4 h-4" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-white">
                {lang === 'ja' ? '毎日午前1:00 定時自動同期 & オフライン保存' : 'Daily 01:00 AM Auto-Sync & Local Storage'}
              </h4>
              <p className="text-[11px] text-teal-400 font-medium">
                {lang === 'ja' ? '深夜に送配電確定値を自動取得しローカルへ保存' : 'Auto-scrapes finalized grid readings overnight'}
              </p>
            </div>
          </div>
          <span className="px-2 py-0.5 rounded-full bg-teal-500/15 border border-teal-500/30 text-teal-300 text-[10px] font-bold">
            {lang === 'ja' ? '定時自動' : 'Scheduled'}
          </span>
        </div>
      </div>

      {/* Portal Credentials Vault */}
      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-5 shadow-xl">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-teal-500/15 text-teal-400 flex items-center justify-center">
              <Key className="w-4 h-4" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-white">{t.credentialsVaultTitle}</h4>
              <p className="text-[11px] text-slate-400">{t.secureStorageNote}</p>
            </div>
          </div>
          {saveSuccess && (
            <span className="text-xs text-emerald-400 flex items-center gap-1 animate-fadeIn">
              <Check className="w-3.5 h-3.5" /> {lang === 'ja' ? '保存完了' : 'Saved'}
            </span>
          )}
        </div>

        <form onSubmit={handleSaveCredentials} className="space-y-3 pt-1">
          <div>
            <label className="block text-[11px] font-medium text-slate-400 mb-1">
              {t.emailField}
            </label>
            <input
              type="text"
              value={credentials.email}
              placeholder={lang === 'ja' ? 'お客さま番号 または メールアドレス' : 'Customer # or Email Address'}
              onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
              className="w-full bg-slate-950/60 border border-slate-800 rounded-xl px-3 py-2 text-xs text-slate-200 outline-none focus:border-teal-500/50"
            />
          </div>

          <div>
            <label className="block text-[11px] font-medium text-slate-400 mb-1">
              {t.passwordField}
            </label>
            <div className="flex items-center bg-slate-950/60 border border-slate-800 rounded-xl px-3 py-2 text-xs">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                placeholder={lang === 'ja' ? 'マイページ パスワード' : 'MyPage Password'}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-transparent text-slate-200 outline-none text-xs"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="text-slate-500 hover:text-slate-300 transition-colors ml-2 cursor-pointer"
              >
                {showPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>

          <div className="flex flex-col gap-2 pt-2">
            <div className="flex items-center justify-between">
              <button
                type="submit"
                disabled={isSavingCreds}
                className={`px-4 py-2.5 font-semibold text-xs rounded-xl transition-all border flex items-center gap-1.5 shadow-md active:scale-95 disabled:opacity-50 cursor-pointer ${
                  saveSuccess
                    ? 'bg-emerald-600 border-emerald-500 text-white shadow-emerald-950/40'
                    : 'bg-teal-600 hover:bg-teal-500 text-white border-teal-500/60 shadow-teal-950/40'
                }`}
              >
                {isSavingCreds ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    <span>{lang === 'ja' ? '保存中...' : 'Saving...'}</span>
                  </>
                ) : saveSuccess ? (
                  <>
                    <Check className="w-3.5 h-3.5" />
                    <span>{lang === 'ja' ? '設定を更新しました！' : 'Credentials Updated!'}</span>
                  </>
                ) : (
                  <>
                    <Key className="w-3.5 h-3.5" />
                    <span>{t.updateCredsBtn}</span>
                  </>
                )}
              </button>
            </div>

            {saveMessage && (
              <div
                className={`p-2.5 rounded-xl text-xs flex items-center gap-2 animate-fadeIn transition-all ${
                  saveSuccess
                    ? 'bg-emerald-500/15 border border-emerald-500/30 text-emerald-300'
                    : 'bg-rose-500/15 border border-rose-500/30 text-rose-300'
                }`}
              >
                {saveSuccess ? (
                  <Check className="w-4 h-4 shrink-0 text-emerald-400" />
                ) : (
                  <AlertCircle className="w-4 h-4 shrink-0 text-rose-400" />
                )}
                <span>{saveMessage}</span>
              </div>
            )}
          </div>
        </form>
      </div>

      {/* Live Scraper Terminal Log & cURL Debugger */}
      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-5 shadow-xl space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-teal-500/15 text-teal-400 flex items-center justify-center">
              <Terminal className="w-4 h-4" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-white">{t.terminalTitle}</h4>
              <p className="text-[11px] text-slate-400">{t.terminalSubtitle}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleClearLogs}
              className="text-[11px] text-slate-500 hover:text-slate-300 py-1 px-2 cursor-pointer"
            >
              {t.clearLogsBtn}
            </button>
          </div>
        </div>

        {/* Log Entries with Expandable cURL */}
        <div className="bg-slate-950 border border-slate-800/80 rounded-2xl p-3 max-h-80 overflow-y-auto space-y-2.5 font-mono text-[11px]">
          {logs.length === 0 ? (
            <p className="text-slate-600 text-center py-4">{t.noLogs}</p>
          ) : (
            logs.map((log) => {
              const isExpanded = expandedCurlId === log.id;
              const hasCurl = Boolean(log.curlCommand);

              return (
                <div key={log.id} className="border-b border-slate-900 pb-2 last:border-0 last:pb-0 space-y-1">
                  <div className="flex items-center justify-between text-slate-500 text-[10px]">
                    <div className="flex items-center gap-2 truncate">
                      <span>[{log.timestamp}]</span>
                      <span className={`font-semibold ${
                        log.status === 'error'
                          ? 'text-rose-400'
                          : log.status === 'warn'
                          ? 'text-amber-400'
                          : 'text-teal-400'
                      }`}>
                        {log.step}
                      </span>
                    </div>

                    <div className="flex items-center gap-1.5 shrink-0">
                      {log.httpStatus && (
                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold border ${
                          log.httpStatus >= 200 && log.httpStatus < 300
                            ? 'bg-emerald-950/70 border-emerald-800/50 text-emerald-300'
                            : 'bg-rose-950/80 border-rose-700 text-rose-300'
                        }`}>
                          HTTP {log.httpStatus}
                        </span>
                      )}

                      {hasCurl && (
                        <button
                          type="button"
                          onClick={() => setExpandedCurlId(isExpanded ? null : log.id)}
                          className="px-1.5 py-0.5 rounded text-[10px] bg-slate-900 hover:bg-slate-800 text-teal-300 border border-slate-700 flex items-center gap-1 transition-colors cursor-pointer"
                        >
                          <Terminal className="w-2.5 h-2.5" />
                          <span>cURL</span>
                          {isExpanded ? <ChevronUp className="w-2.5 h-2.5" /> : <ChevronDown className="w-2.5 h-2.5" />}
                        </button>
                      )}
                    </div>
                  </div>

                  {log.requestUrl && (
                    <div className="text-[10px] text-slate-500 truncate flex items-center gap-1">
                      <span className="text-slate-600">URL:</span>
                      <span className="text-slate-400 font-mono truncate">{log.requestUrl}</span>
                    </div>
                  )}

                  <p className="text-slate-300 text-[11px] leading-relaxed">
                    {log.details}
                  </p>

                  {/* Expandable cURL view */}
                  {isExpanded && log.curlCommand && (
                    <div className="mt-1.5 p-2.5 rounded-xl bg-slate-900 border border-teal-500/30 text-[10px] text-slate-200 space-y-1.5 animate-fadeIn">
                      <div className="flex items-center justify-between text-slate-400 text-[9px] pb-1 border-b border-slate-800">
                        <span className="font-semibold text-teal-400">{t.viewCurlCommand}</span>
                        <button
                          type="button"
                          onClick={() => handleCopyCurl(log.id, log.curlCommand!)}
                          className="px-2 py-0.5 rounded bg-teal-500/20 hover:bg-teal-500/30 text-teal-300 border border-teal-500/40 flex items-center gap-1 transition-colors cursor-pointer"
                        >
                          {copiedCurlId === log.id ? (
                            <>
                              <Check className="w-2.5 h-2.5 text-emerald-400" />
                              <span>{t.copiedCurlBtn}</span>
                            </>
                          ) : (
                            <>
                              <Copy className="w-2.5 h-2.5" />
                              <span>{t.copyCurlBtn}</span>
                            </>
                          )}
                        </button>
                      </div>
                      <pre className="overflow-x-auto whitespace-pre font-mono text-teal-300/90 leading-tight p-1 select-all">
                        {log.curlCommand}
                      </pre>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
